import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Link2, Loader2, Unlink, User } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { discordService, type MyDiscordLink } from '@/lib/services/discord.service';
import { DiscordOfferInfo, DiscordRewardInfo } from './DiscordRewardInfo';

/**
 * O vínculo da conta do Discord, no perfil do jogador.
 *
 * Location: src/components/discord
 * Why: o vínculo só começa aqui, com o jogador logado. O botão leva ao
 * Discord com um `state` preso a esta sessão, e é isso que impede alguém de
 * vincular o próprio Discord à conta de outra pessoa (ver o plano, §5.2).
 */
export function DiscordLinkCard({
  storeId,
  storeName,
  data,
  onChange,
}: {
  storeId: string;
  storeName: string;
  data: MyDiscordLink;
  onChange: (data: MyDiscordLink) => void;
}) {
  const { t, i18n } = useTranslation();
  const [indo, setIndo] = useState(false);
  const [desvinculando, setDesvinculando] = useState(false);
  const link = data.link;

  async function vincular() {
    setIndo(true);
    try {
      const url = await discordService.start(storeId);
      // Só segue para o Discord. Qualquer outro destino seria a API respondendo errado.
      if (!url.startsWith('https://discord.com/')) throw new Error('destino inesperado');
      window.location.assign(url);
    } catch {
      toast.error(t('store.discord.error'));
      setIndo(false);
    }
  }

  async function desvincular() {
    setDesvinculando(true);
    try {
      await discordService.unlink(storeId);
      onChange({ ...data, link: null });
      toast.success(t('store.discord.unlinked'));
    } catch {
      toast.error(t('store.discord.error'));
    } finally {
      setDesvinculando(false);
    }
  }

  return (
    <Card className="glass-strong border-border/50" data-testid="discord-link-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-2xl">
          <Link2 className="h-6 w-6 text-[#5865F2]" />
          {t('store.discord.title')}
        </CardTitle>
        <CardDescription>{t('store.discord.description', { store: storeName })}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {data.reward && <DiscordRewardInfo reward={data.reward} />}
        {link ? (
          <div className="flex flex-wrap items-center gap-4 rounded-xl border border-border/50 bg-muted/30 p-4">
            <Avatar className="h-12 w-12">
              {link.avatarUrl && <AvatarImage src={link.avatarUrl} alt="" />}
              <AvatarFallback className="bg-[#5865F2] text-white">
                <User className="h-6 w-6" />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{t('store.discord.linked_as', { username: link.username })}</p>
              <p className="text-sm text-muted-foreground">
                {t('store.discord.linked_since', {
                  date: new Date(link.linkedAt).toLocaleDateString(i18n.language),
                })}
                {data.guildName ? ` · ${t('store.discord.server', { name: data.guildName })}` : ''}
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" disabled={desvinculando}>
                  {desvinculando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Unlink className="mr-2 h-4 w-4" />}
                  {t('store.discord.unlink_button')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('store.discord.unlink_confirm')}</AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('store.discord.callback_back')}</AlertDialogCancel>
                  <AlertDialogAction onClick={desvincular}>{t('store.discord.unlink_button')}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ) : (
          <div className="space-y-3">
            {data.offer && <DiscordOfferInfo offer={data.offer} />}
            <p className="text-sm text-muted-foreground">{t('store.discord.how_it_works', { store: storeName })}</p>
            <Button onClick={vincular} disabled={indo} className="bg-[#5865F2] text-white hover:bg-[#4752c4]">
              {indo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" />}
              {indo ? t('store.discord.redirecting') : t('store.discord.link_button')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
